'use client';

import React, { useState } from 'react';
import Link from 'next/link';

interface FormState {
  name: string;
  email: string;
  phone: string;
  message: string;
}

export default function ContactPageContent() {
  const [formState, setFormState] = useState<FormState>({ name: '', email: '', phone: '', message: '' });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormState((prev) => ({ ...prev, [e.target.name]: e.target.value }));
    if (error) setError('');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const response = await fetch('/api/contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formState),
      });

      const data = await response.json();

      if (!response.ok) {
        setError(data.error || 'Failed to send your message. Please try again.');
      } else {
        setSubmitted(true);
      }
    } catch {
      setError('An unexpected error occurred. Please try again or contact us directly.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <section className="py-16 lg:py-24">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-12 gap-12 lg:gap-16">

          {/* Left: Contact Info */}
          <div className="lg:col-span-5 space-y-8">
            <div>
              <h2 className="font-display text-foreground text-2xl mb-2">Get in Touch</h2>
              <p className="text-muted-foreground leading-relaxed">
                Reach out to EWIN Lifescience for product enquiries, business discussions, or any healthcare-related information.
              </p>
            </div>

            <div className="space-y-0 divide-y divide-border">
              {/* Phone */}
              <div className="py-5 flex items-start gap-4">
                <div className="w-11 h-11 bg-blue-50 text-primary rounded-xl flex items-center justify-center flex-shrink-0">
                  <svg width="22" height="22" viewBox="0 0 22 22" fill="none" aria-hidden="true">
                    <path d="M3 5C3 3.9 3.9 3 5 3H7.28C7.67 3 8 3.27 8.1 3.65L9.07 7.35C9.18 7.77 8.97 8.21 8.57 8.4L7.12 9.08C7.93 10.72 9.28 12.07 10.92 12.88L11.6 11.43C11.79 11.03 12.23 10.82 12.65 10.93L16.35 11.9C16.73 12 17 12.33 17 12.72V15C17 16.1 16.1 17 15 17C8.37 17 3 11.63 3 5Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </div>
                <div>
                  <p className="text-[10px] font-bold tracking-widest uppercase text-muted-foreground mb-1">Phone</p>
                  <a href="tel:+916376769003" className="text-foreground font-semibold text-lg hover:text-primary transition-colors">
                    +91 6376 769 003
                  </a>
                </div>
              </div>

              {/* Email */}
              <div className="py-5 flex items-start gap-4">
                <div className="w-11 h-11 bg-blue-50 text-primary rounded-xl flex items-center justify-center flex-shrink-0">
                  <svg width="22" height="22" viewBox="0 0 22 22" fill="none" aria-hidden="true">
                    <path d="M3 6L11 12L19 6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                    <rect x="3" y="5" width="16" height="12" rx="2" stroke="currentColor" strokeWidth="1.5"/>
                  </svg>
                </div>
                <div>
                  <p className="text-[10px] font-bold tracking-widest uppercase text-muted-foreground mb-1">Email</p>
                  <a href="mailto:ewinlifescience2005@gmail.com" className="text-foreground font-semibold hover:text-primary transition-colors break-all">
                    ewinlifescience2005@gmail.com
                  </a>
                </div>
              </div>

              {/* Instagram */}
              <div className="py-5 flex items-start gap-4">
                <div className="w-11 h-11 bg-blue-50 text-primary rounded-xl flex items-center justify-center flex-shrink-0">
                  <svg width="22" height="22" viewBox="0 0 22 22" fill="none" aria-hidden="true">
                    <rect x="3" y="3" width="16" height="16" rx="5" stroke="currentColor" strokeWidth="1.5"/>
                    <circle cx="11" cy="11" r="3.5" stroke="currentColor" strokeWidth="1.5"/>
                    <circle cx="15.5" cy="6.5" r="1" fill="currentColor"/>
                  </svg>
                </div>
                <div>
                  <p className="text-[10px] font-bold tracking-widest uppercase text-muted-foreground mb-1">Instagram</p>
                  <a
                    href="https://www.instagram.com/ewinlifesciences2005"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-foreground font-semibold hover:text-primary transition-colors"
                  >
                    @ewinlifesciences2005
                  </a>
                </div>
              </div>

              {/* Address */}
              <div className="py-5 flex items-start gap-4">
                <div className="w-11 h-11 bg-blue-50 text-primary rounded-xl flex items-center justify-center flex-shrink-0 mt-0.5">
                  <svg width="22" height="22" viewBox="0 0 22 22" fill="none" aria-hidden="true">
                    <path d="M11 2C7.7 2 5 4.7 5 8C5 12.5 11 20 11 20C11 20 17 12.5 17 8C17 4.7 14.3 2 11 2Z" stroke="currentColor" strokeWidth="1.5"/>
                    <circle cx="11" cy="8" r="2.5" stroke="currentColor" strokeWidth="1.5"/>
                  </svg>
                </div>
                <div>
                  <p className="text-[10px] font-bold tracking-widest uppercase text-muted-foreground mb-1">Office Address</p>
                  <address className="not-italic text-foreground font-medium leading-relaxed">
                    Khasra No., Kunda Ki Dhani,<br />
                    Sirsi, Gandhi Path,<br />
                    Jaipur, Rajasthan 302034
                  </address>
                </div>
              </div>
            </div>

            {/* Back to Home */}
            <div className="pt-2">
              <Link href="/" className="pharma-btn-secondary inline-flex">
                <svg width="18" height="18" viewBox="0 0 18 18" fill="none" aria-hidden="true">
                  <path d="M15 9H3M8 4L3 9L8 14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                Back to Home
              </Link>
            </div>
          </div>

          {/* Right: Contact Form */}
          <div className="lg:col-span-7">
            <div className="bg-card rounded-2xl border border-border p-8 shadow-sm">
              {submitted ? (
                <div className="text-center py-16 space-y-5">
                  <div className="w-20 h-20 bg-green-50 text-secondary rounded-full flex items-center justify-center mx-auto">
                    <svg width="40" height="40" viewBox="0 0 40 40" fill="none" aria-hidden="true">
                      <path d="M8 20L16 28L32 12" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </div>
                  <div>
                    <h3 className="font-display text-2xl text-foreground mb-2">Thank You for Your Inquiry!</h3>
                    <p className="text-muted-foreground max-w-sm mx-auto leading-relaxed">
                      Thank you for your inquiry. We will get back to you shortly.
                    </p>
                  </div>
                  <button
                    onClick={() => { setSubmitted(false); setFormState({ name: '', email: '', phone: '', message: '' }); setError(''); }}
                    className="pharma-btn-secondary"
                  >
                    Send Another Message
                  </button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div>
                    <h2 className="font-display text-2xl text-foreground mb-1">Send a Message</h2>
                    <p className="text-muted-foreground text-sm">Fill in the form below and we will get back to you.</p>
                  </div>

                  {error && (
                    <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg px-4 py-3 text-sm flex items-start gap-2">
                      <svg width="18" height="18" viewBox="0 0 18 18" fill="none" className="flex-shrink-0 mt-0.5" aria-hidden="true">
                        <circle cx="9" cy="9" r="7.5" stroke="currentColor" strokeWidth="1.5"/>
                        <path d="M9 5.5V9.5M9 12.5h.01" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                      </svg>
                      {error}
                    </div>
                  )}

                  <div className="grid sm:grid-cols-2 gap-5">
                    <div className="space-y-1.5">
                      <label htmlFor="contact-name" className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                        Full Name *
                      </label>
                      <input
                        id="contact-name"
                        name="name"
                        type="text"
                        required
                        value={formState.name}
                        onChange={handleChange}
                        placeholder="Your full name"
                        className="form-input"
                        disabled={loading}
                      />
                    </div>
                    <div className="space-y-1.5">
                      <label htmlFor="contact-email" className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                        Email Address *
                      </label>
                      <input
                        id="contact-email"
                        name="email"
                        type="email"
                        required
                        value={formState.email}
                        onChange={handleChange}
                        placeholder="your@email.com"
                        className="form-input"
                        disabled={loading}
                      />
                    </div>
                  </div>

                  <div className="space-y-1.5">
                    <label htmlFor="contact-phone" className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                      Phone Number
                    </label>
                    <input
                      id="contact-phone"
                      name="phone"
                      type="tel"
                      value={formState.phone}
                      onChange={handleChange}
                      placeholder="+91 XXXXX XXXXX"
                      className="form-input"
                      disabled={loading}
                    />
                  </div>

                  <div className="space-y-1.5">
                    <label htmlFor="contact-message" className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                      Message *
                    </label>
                    <textarea
                      id="contact-message"
                      name="message"
                      required
                      rows={6}
                      value={formState.message}
                      onChange={handleChange}
                      placeholder="How can we help you? Please describe your enquiry..."
                      className="form-input resize-none"
                      disabled={loading}
                    />
                  </div>

                  <button
                    type="submit"
                    className="pharma-btn-primary w-full justify-center text-base py-4"
                    disabled={loading}
                    style={{ opacity: loading ? 0.7 : 1, cursor: loading ? 'not-allowed' : 'pointer' }}
                  >
                    {loading ? (
                      <>
                        <svg width="20" height="20" viewBox="0 0 20 20" fill="none" className="animate-spin" aria-hidden="true">
                          <circle cx="10" cy="10" r="8" stroke="currentColor" strokeWidth="2" strokeDasharray="25 50" strokeLinecap="round"/>
                        </svg>
                        Sending...
                      </>
                    ) : (
                      <>
                        Send Message
                        <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true">
                          <path d="M3 10H17M11 4l6 6-6 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                      </>
                    )}
                  </button>

                  <p className="text-xs text-muted-foreground text-center leading-relaxed">
                    Your information is kept confidential and will only be used to respond to your enquiry.
                    Medicines should be used only as directed by a qualified healthcare professional.
                  </p>
                </form>
              )}
            </div>
          </div>
        </div>

        {/* Map placeholder / Location visual */}
        <div className="mt-16 rounded-2xl overflow-hidden border border-border bg-gradient-to-br from-blue-50 to-green-50 h-[200px] flex items-center justify-center">
          <div className="text-center space-y-3">
            <div className="w-12 h-12 bg-primary text-primary-foreground rounded-full flex items-center justify-center mx-auto">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <path d="M12 2C8.1 2 5 5.1 5 9C5 14.2 12 22 12 22C12 22 19 14.2 19 9C19 5.1 15.9 2 12 2Z" stroke="currentColor" strokeWidth="2"/>
                <circle cx="12" cy="9" r="3" stroke="currentColor" strokeWidth="2"/>
              </svg>
            </div>
            <div>
              <p className="font-semibold text-foreground">EWIN Lifescience</p>
              <p className="text-sm text-muted-foreground">Kunda Ki Dhani, Sirsi, Gandhi Path, Jaipur, Rajasthan 302034</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}